import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getFirestore, collection, query, orderBy, limit, getDocs, where, doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './App.css';

const ADMIN_UIDS = ['GI1GHQ7wpLQKjJyf9QRQATyw5e33', 'vGB6BL3iJfh32aWZ0HA93nUV2013']; // Lawrence + ChallengePact

// Health pill for the weekly Model Catalog Sync cron. Reads
// system_status/modelCatalogSync written by scheduledModelCatalogSync.
// Colors: green <8d, yellow 8-14d, red >14d or failure. Click to expand.
function ModelCatalogSyncPill() {
  const [status, setStatus] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const db = getFirestore();
    getDoc(doc(db, 'system_status', 'modelCatalogSync'))
      .then(snap => setStatus(snap.exists() ? snap.data() : null))
      .catch(err => console.warn('[ModelCatalogSyncPill] load failed:', err.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!status) {
    return (
      <span style={{ color: '#64748b', fontWeight: 500, fontSize: '0.85rem' }}>
        ⚪ Catalog Sync: not run yet
      </span>
    );
  }

  const ranAt = status.lastRanAtISO ? new Date(status.lastRanAtISO) : null;
  const ageMs = ranAt ? Date.now() - ranAt.getTime() : null;
  const ageDays = ageMs != null ? Math.floor(ageMs / (1000 * 60 * 60 * 24)) : null;
  const isFailure = status.lastOutcome === 'failure';

  let color = '#10b981', icon = '🟢';
  if (isFailure || (ageDays != null && ageDays > 14)) { color = '#ef4444'; icon = '🔴'; }
  else if (ageDays != null && ageDays > 7) { color = '#f59e0b'; icon = '🟡'; }

  const ageLabel = ageDays == null ? 'unknown' : ageDays === 0 ? 'today' : `${ageDays}d ago`;

  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      <button
        onClick={() => setExpanded(!expanded)}
        title={`Last outcome: ${status.lastOutcome}. ${status.openRouterModelsSeen ?? 0} models seen on OpenRouter. Click for details.`}
        style={{
          background: 'transparent', border: 'none', color, fontWeight: 500, fontSize: '0.85rem',
          cursor: 'pointer', padding: 0, fontFamily: 'inherit', textAlign: 'left'
        }}>
        {icon} Catalog Sync: {ageLabel}{isFailure ? ' — FAILED' : ''}
      </button>
      {expanded && (
        <div style={{
          marginTop: 8, padding: 12, background: '#0b1220',
          borderLeft: `3px solid ${color}`, borderRadius: 6,
          maxHeight: 480, overflow: 'auto', minWidth: 480
        }}>
          {isFailure && status.lastError && (
            <div style={{ color: '#fca5a5', fontFamily: 'monospace', fontSize: '0.75rem', marginBottom: 8 }}>
              Error: {status.lastError}
            </div>
          )}
          {status.lastSummaryMarkdown && (
            <pre style={{
              color: '#cbd5e1', whiteSpace: 'pre-wrap', margin: 0,
              fontFamily: 'monospace', fontSize: '0.7rem'
            }}>
              {status.lastSummaryMarkdown}
            </pre>
          )}
          {Array.isArray(status.lastCommits) && status.lastCommits.length > 0 && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #1e293b' }}>
              <div style={{ color: '#94a3b8', fontSize: '0.7rem', marginBottom: 6 }}>Last commits:</div>
              {status.lastCommits.map(c => (
                <a key={c.sha} href={c.commitURL} target="_blank" rel="noopener noreferrer"
                   style={{ color: '#3b82f6', display: 'block', fontSize: '0.7rem', textDecoration: 'none', marginBottom: 4 }}>
                  {c.providerID}: {c.sha?.slice(0, 7)} — added={c.addedCount ?? 0}, priceChanged={c.priceChangedCount ?? 0}, retained={c.retainedCount ?? 0}
                </a>
              ))}
            </div>
          )}
          <div style={{ marginTop: 10, fontSize: '0.65rem', color: '#64748b' }}>
            Runbook: <code>mchataiweb/docs/MODEL_CATALOG_SYNC.md</code>
          </div>
        </div>
      )}
    </span>
  );
}

// Health pill for the weekly News Feeds Sync cron. Reads
// system_status/newsFeedsSync written by scheduledNewsFeedsSync (health-checks
// mchatai-source/news/feeds.json — the AI News applet's source list). Colors:
// green <8d, yellow 8-14d, red >14d or failure or any dead feed. Click to expand.
function NewsFeedsSyncPill() {
  const [status, setStatus] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const db = getFirestore();
    getDoc(doc(db, 'system_status', 'newsFeedsSync'))
      .then(snap => setStatus(snap.exists() ? snap.data() : null))
      .catch(err => console.warn('[NewsFeedsSyncPill] load failed:', err.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!status) {
    return (
      <span style={{ color: '#64748b', fontWeight: 500, fontSize: '0.85rem' }}>
        ⚪ News Feeds Sync: not run yet
      </span>
    );
  }

  const ranAt = status.lastRanAtISO ? new Date(status.lastRanAtISO) : null;
  const ageMs = ranAt ? Date.now() - ranAt.getTime() : null;
  const ageDays = ageMs != null ? Math.floor(ageMs / (1000 * 60 * 60 * 24)) : null;
  const isFailure = status.lastOutcome === 'failure';
  const deadCount = status.deadCount ?? 0;

  let color = '#10b981', icon = '🟢';
  if (isFailure || (ageDays != null && ageDays > 14)) { color = '#ef4444'; icon = '🔴'; }
  else if (deadCount > 0 || (ageDays != null && ageDays > 7)) { color = '#f59e0b'; icon = '🟡'; }

  const ageLabel = ageDays == null ? 'unknown' : ageDays === 0 ? 'today' : `${ageDays}d ago`;
  const counts = `${status.okCount ?? 0} ok / ${status.flakyCount ?? 0} flaky / ${deadCount} dead of ${status.totalSources ?? 0}`;

  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      <button
        onClick={() => setExpanded(!expanded)}
        title={`Last outcome: ${status.lastOutcome}. ${counts}. Click for details.`}
        style={{
          background: 'transparent', border: 'none', color, fontWeight: 500, fontSize: '0.85rem',
          cursor: 'pointer', padding: 0, fontFamily: 'inherit', textAlign: 'left'
        }}>
        {icon} News Feeds Sync: {ageLabel}{isFailure ? ' — FAILED' : deadCount > 0 ? ` — ${deadCount} dead` : ''}
      </button>
      {expanded && (
        <div style={{
          marginTop: 8, padding: 12, background: '#0b1220',
          borderLeft: `3px solid ${color}`, borderRadius: 6,
          maxHeight: 480, overflow: 'auto', minWidth: 480
        }}>
          <div style={{ color: '#94a3b8', fontSize: '0.72rem', marginBottom: 8 }}>{counts}</div>
          {isFailure && status.lastError && (
            <div style={{ color: '#fca5a5', fontFamily: 'monospace', fontSize: '0.75rem', marginBottom: 8 }}>
              Error: {status.lastError}
            </div>
          )}
          {status.lastSummaryMarkdown && (
            <pre style={{
              color: '#cbd5e1', whiteSpace: 'pre-wrap', margin: 0,
              fontFamily: 'monospace', fontSize: '0.7rem'
            }}>
              {status.lastSummaryMarkdown}
            </pre>
          )}
          {status.lastCommit?.commitURL && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #1e293b' }}>
              <a href={status.lastCommit.commitURL} target="_blank" rel="noopener noreferrer"
                 style={{ color: '#3b82f6', fontSize: '0.7rem', textDecoration: 'none' }}>
                Last commit: {status.lastCommit.sha?.slice(0, 7)} — {status.lastCommit.message || 'feed health update'}
              </a>
            </div>
          )}
          <div style={{ marginTop: 10, fontSize: '0.65rem', color: '#64748b' }}>
            Source of truth: <code>mchatai-source/news/feeds.json</code> · add feeds by PR (zero rebuild)
          </div>
        </div>
      )}
    </span>
  );
}

// Sectioned card grid for navigating the /admin/* sub-pages. Add new pages here
// when they ship — single source of truth for the admin nav.
const ADMIN_DIRECTORY = [
  {
    section: 'Review queues',
    blurb: 'User-generated content awaiting promotion to mchatai-source.',
    cards: [
      { to: '/admin/template-candidates',     title: 'Template Candidates',     desc: 'Triage hub apps. Run discriminator, then promote or reject.' },
      { to: '/admin/component-candidates',    title: 'Component Candidates',    desc: 'Web components discovered from user mini-apps awaiting catalog promotion.' },
      { to: '/admin/context-map-candidates',  title: 'Context Map Candidates',  desc: 'PEEK recipe context-map distillations awaiting review.' },
      { to: '/admin/nudges',                  title: 'Nudges',                  desc: 'Pending evaluations from your Mac — LEARN wisdom-rule proposals + borrow keep/revert triggers. Read + triage.' },
      { to: '/admin/feedback',                title: 'Feedback → Improve',      desc: 'Community feedback per app, prioritized by rating × volume. Branch & Improve queues a 2.0 build on your Mac.' },
    ]
  },
  {
    section: 'Live catalogs',
    blurb: 'Browse and curate what is live in mchatai-source and the platform.',
    cards: [
      { to: '/admin/templates',     title: 'Templates Catalog',  desc: 'Every promoted AIWizard template. Replace, demote, or inspect.' },
      { to: '/admin/model-catalog', title: 'Model Catalog',      desc: 'LLM model catalog. Weekly sync status, manual refresh, recent additions.' },
      { to: '/admin/wisdom',        title: 'Wizard Wisdom',      desc: 'Wisdom rules + correction candidates. Publish to community.' },
      { to: '/admin/prompts',       title: 'Prompt Catalog',     desc: 'Community-published prompts. Remove abuse, ban publishers, review moderation rejections.' },
      { to: 'https://mchatai.com/hub-trademark-scan', external: true, title: 'Trademark Scan', desc: 'Pre-ship gate: scans the live catalog for trademarked clone titles + descriptions. Run before every iOS/macOS submission (Apple 4.7/4.1).' },
    ]
  },
  {
    section: 'Content & operations',
    blurb: 'Production publishing, demo runs, and platform internals.',
    cards: [
      { to: '/admin/hub',  title: 'Hub Cleanup & Relations',  desc: 'Prune duplicates, QA artifacts, stale variants, and inspect related/offshoot candidates.' },
      { to: '/admin/blog', title: 'Blog',         desc: 'Blog drafts → published posts queue. Review, edit, and ship.' },
      { to: '/admin/tm',   title: 'TM Hub',       desc: 'Testing & Marketing demo runs across Macs. Sampled thumbnails + video.' },
      { to: '/admin/ops',  title: 'Ops',          desc: 'Enforcement mode, env viewer, debug tokens, rate limits.' },
    ]
  },
];

function AdminPageDirectory() {
  return (
    <div style={{ marginBottom: 24 }}>
      {ADMIN_DIRECTORY.map(section => (
        <div key={section.section} style={{ marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 8 }}>
            <h2 style={{
              color: '#94a3b8', fontSize: '0.72rem', fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: 0.8, margin: 0
            }}>{section.section}</h2>
            <span style={{ color: '#64748b', fontSize: '0.78rem' }}>{section.blurb}</span>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: 10
          }}>
            {section.cards.map(card => {
              const inner = (
                <>
                  <div style={{ color: '#f8fafc', fontSize: '0.95rem', fontWeight: 600, marginBottom: 4 }}>
                    {card.title} <span style={{ color: '#3b82f6' }}>{card.external ? '↗' : '→'}</span>
                  </div>
                  <div style={{ color: '#94a3b8', fontSize: '0.78rem', lineHeight: 1.35 }}>
                    {card.desc}
                  </div>
                </>
              );
              return card.external ? (
                <a key={card.to} href={card.to} target="_blank" rel="noopener noreferrer" style={adminDirectoryCardStyle}>{inner}</a>
              ) : (
                <Link key={card.to} to={card.to} style={adminDirectoryCardStyle}>{inner}</Link>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

const adminDirectoryCardStyle = {
  display: 'block', textDecoration: 'none',
  background: '#1e293b', border: '1px solid #334155', borderRadius: 8,
  padding: '12px 14px', transition: 'border-color 120ms, background 120ms'
};

// Phase IM.4 (2026-05-15) — Provider Routing panel. Fetches /v1/admin/status
// from the compute-proxy with the user's Firebase ID token. Shows current
// routing strategy per endpoint, key configuration, 24h call counts + token
// spend, and a cost-vs-revenue read.
function ProviderRoutingPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchStatus() {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) { setError('Not signed in'); setLoading(false); return; }
        const token = await user.getIdToken();
        const resp = await fetch('https://api.mchatai.com/v1/admin/status', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!resp.ok) {
          const text = await resp.text();
          if (!cancelled) { setError(`HTTP ${resp.status}: ${text.slice(0, 200)}`); setLoading(false); }
          return;
        }
        const json = await resp.json();
        if (!cancelled) { setStatus(json); setLoading(false); }
      } catch (e) {
        if (!cancelled) { setError(e.message); setLoading(false); }
      }
    }
    fetchStatus();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div style={{ padding: 20, color: '#94a3b8' }}>Loading provider routing status…</div>;
  if (error) return <div style={{ padding: 20, color: '#fca5a5' }}>Failed: {error}</div>;
  if (!status) return null;

  const routeColor = (route) => {
    if (route === 'openrouter') return '#10b981';
    if (route?.startsWith('direct-')) return '#f59e0b';
    return '#64748b';
  };

  const { last24h, endpoints, flags } = status;
  const margin = last24h.revenueUsd > 0
    ? ((last24h.revenueUsd - last24h.costToProxyUsd) / last24h.revenueUsd * 100)
    : 0;
  const marginColor = margin >= 15 ? '#10b981' : margin >= 0 ? '#f59e0b' : '#ef4444';

  return (
    <div style={{ background: '#1e293b', borderRadius: 12, padding: 20, marginBottom: 20 }}>
      <h2 style={{ color: '#f8fafc', fontSize: '1.15rem', marginBottom: 4 }}>Provider Routing & Cost (24h)</h2>
      <div style={{ color: '#64748b', fontSize: '0.8rem', marginBottom: 16 }}>
        Compute-proxy upstream routing per endpoint. Last 24h calls + token deductions from audit_log.
      </div>

      {/* Cost vs revenue summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
        <div style={{ background: '#0b1220', padding: 12, borderRadius: 8 }}>
          <div style={{ color: '#94a3b8', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Calls (24h)</div>
          <div style={{ color: '#f8fafc', fontSize: '1.6rem', fontWeight: 700 }}>{last24h.totalCalls.toLocaleString()}</div>
        </div>
        <div style={{ background: '#0b1220', padding: 12, borderRadius: 8 }}>
          <div style={{ color: '#94a3b8', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Tokens deducted</div>
          <div style={{ color: '#f8fafc', fontSize: '1.6rem', fontWeight: 700 }}>{last24h.totalTokensDeducted.toFixed(2)}</div>
        </div>
        <div style={{ background: '#0b1220', padding: 12, borderRadius: 8 }}>
          <div style={{ color: '#94a3b8', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Upstream cost</div>
          <div style={{ color: '#f8fafc', fontSize: '1.6rem', fontWeight: 700 }}>${last24h.costToProxyUsd.toFixed(2)}</div>
          <div style={{ color: '#64748b', fontSize: '0.65rem', marginTop: 2 }}>est. raw provider spend</div>
        </div>
        <div style={{ background: '#0b1220', padding: 12, borderRadius: 8 }}>
          <div style={{ color: '#94a3b8', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Revenue value</div>
          <div style={{ color: '#f8fafc', fontSize: '1.6rem', fontWeight: 700 }}>${last24h.revenueUsd.toFixed(2)}</div>
          <div style={{ color: marginColor, fontSize: '0.65rem', marginTop: 2, fontWeight: 600 }}>margin {margin.toFixed(1)}%</div>
        </div>
      </div>

      {/* Flag row */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, fontSize: '0.7rem' }}>
        {Object.entries(flags).map(([k, v]) => (
          <span key={k} style={{
            padding: '4px 10px',
            background: v ? 'rgba(16,185,129,0.15)' : 'rgba(100,116,139,0.15)',
            color: v ? '#10b981' : '#94a3b8',
            borderRadius: 6,
            fontFamily: 'monospace'
          }}>
            {k}={String(v)}
          </span>
        ))}
      </div>

      {/* IM.7 — same-model failover counter. Sustained non-zero = the
          direct path is sick (key yanked / billing depleted / rate-limit
          / upstream outage). "successes" means OR caught the issue
          cleanly. byReason shows the HTTP status that triggered each
          failover; byModel shows which models are hitting it. */}
      {last24h.openrouterFailovers && (() => {
        const fo = last24h.openrouterFailovers;
        const hasActivity = fo.total > 0 || fo.successes > 0 || fo.failures > 0;
        const sustainedConcern = fo.total >= 10; // arbitrary noise threshold
        const successRate = fo.total > 0 ? Math.round((fo.successes / fo.total) * 100) : 0;
        return (
          <div style={{
            background: sustainedConcern ? 'rgba(245,158,11,0.10)' : '#0b1220',
            borderRadius: 8,
            padding: 12,
            marginBottom: 16,
            borderLeft: `3px solid ${sustainedConcern ? '#f59e0b' : (hasActivity ? '#10b981' : '#64748b')}`
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ color: '#f8fafc', fontSize: '0.85rem', fontWeight: 600 }}>
                  Direct → OpenRouter failover (same-model safety net)
                </div>
                <div style={{ color: '#94a3b8', fontSize: '0.7rem', marginTop: 2 }}>
                  When direct providers return 401/403/429/5xx, router retries the same model via OR.
                  {!hasActivity && ' No failovers fired in last 24h — primary path is healthy.'}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: hasActivity ? (sustainedConcern ? '#f59e0b' : '#10b981') : '#64748b', fontSize: '1.4rem', fontWeight: 700 }}>
                  {fo.total}
                </div>
                <div style={{ color: '#64748b', fontSize: '0.65rem' }}>attempts (24h)</div>
                {fo.total > 0 && (
                  <div style={{ color: successRate >= 80 ? '#10b981' : '#f59e0b', fontSize: '0.7rem', marginTop: 2, fontWeight: 600 }}>
                    {fo.successes} succeeded · {successRate}% rate
                  </div>
                )}
              </div>
            </div>
            {hasActivity && (
              <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <div style={{ color: '#94a3b8', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>By trigger status</div>
                  {Object.entries(fo.byReason || {}).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([reason, count]) => (
                    <div key={reason} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#cbd5e1', padding: '2px 0' }}>
                      <code style={{ color: '#f59e0b' }}>HTTP {reason}</code>
                      <span style={{ fontFamily: 'monospace' }}>{count}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <div style={{ color: '#94a3b8', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Top affected models</div>
                  {Object.entries(fo.byModel || {}).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([modelKey, count]) => (
                    <div key={modelKey} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#cbd5e1', padding: '2px 0' }}>
                      <code style={{ color: '#cbd5e1' }}>{modelKey}</code>
                      <span style={{ fontFamily: 'monospace' }}>{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Per-endpoint table */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {endpoints.map((ep) => {
          const allKeysConfigured = Object.values(ep.keysConfigured || {}).every(Boolean);
          return (
            <div key={ep.path} style={{
              background: '#0b1220',
              padding: 14,
              borderRadius: 8,
              borderLeft: `3px solid ${routeColor(ep.route)}`
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <code style={{ color: '#f8fafc', fontWeight: 600 }}>{ep.path}</code>
                  <div style={{ color: '#94a3b8', fontSize: '0.7rem', marginTop: 4, lineHeight: 1.4 }}>
                    {ep.useCases}
                  </div>
                  {ep.migratableReason && (
                    <div style={{ color: '#f59e0b', fontSize: '0.65rem', marginTop: 4, fontStyle: 'italic' }}>
                      Stays direct: {ep.migratableReason}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: 'right', minWidth: 160 }}>
                  <div style={{ color: routeColor(ep.route), fontSize: '0.75rem', fontWeight: 600, fontFamily: 'monospace' }}>
                    {ep.route}
                  </div>
                  <div style={{ color: '#cbd5e1', fontSize: '0.7rem', marginTop: 4 }}>
                    {ep.stats.calls} calls · {ep.stats.tokens.toFixed(2)} tokens
                  </div>
                  <div style={{
                    color: allKeysConfigured ? '#10b981' : '#ef4444',
                    fontSize: '0.65rem',
                    marginTop: 4,
                    fontFamily: 'monospace'
                  }}>
                    {allKeysConfigured ? '✓ keys configured' : '✗ keys missing'}
                  </div>
                </div>
              </div>
              {/* Key list */}
              <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {Object.entries(ep.keysConfigured || {}).map(([key, ok]) => (
                  <span key={key} style={{
                    padding: '2px 8px',
                    background: ok ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                    color: ok ? '#10b981' : '#ef4444',
                    borderRadius: 4,
                    fontSize: '0.65rem',
                    fontFamily: 'monospace'
                  }}>
                    {ok ? '✓' : '✗'} {key}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 12, color: '#64748b', fontSize: '0.65rem', fontStyle: 'italic' }}>
        Flip env vars + redeploy compute-proxy to migrate: <code>TTS_VIA_OPENROUTER=true</code>, <code>IMAGES_VIA_OPENROUTER=true</code>, <code>OPENROUTER_ENABLED=true</code>.
        See <code>mchataiweb/cloudrun/services/compute-proxy/deploy.sh</code>.
      </div>
    </div>
  );
}

export default function AdminSecurityPage() {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  // Security data
  const [auditLogs, setAuditLogs] = useState([]);
  const [securityStats, setSecurityStats] = useState({ totalCalls: 0, abuseFlags: 0, rateLimits: 0, authFailures: 0 });
  const [auditFilter, setAuditFilter] = useState('all');

  // Grant tokens
  const [grantUid, setGrantUid] = useState('');
  const [grantAmount, setGrantAmount] = useState('');
  const [grantReason, setGrantReason] = useState('');
  const [grantStatus, setGrantStatus] = useState('');
  const [grantLoading, setGrantLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');

  // Edit user (tier + roles + caps)
  const [editingUid, setEditingUid] = useState('');
  const [updateStatus, setUpdateStatus] = useState('');
  const [updateLoading, setUpdateLoading] = useState(false);

  // Account lookup
  const [lookupEmail, setLookupEmail] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Revenue data
  const [revenueStats, setRevenueStats] = useState({
    totalTokensSold: 0, totalRevenue: 0, totalTokensConsumed: 0, totalCostToUs: 0,
    grossMargin: 0, activeUsers: 0, avgTokensPerUser: 0,
  });
  const [userBalances, setUserBalances] = useState([]);

  useEffect(() => {
    const auth = getAuth();
    const unsub = auth.onAuthStateChanged(async (u) => {
      setUser(u);
      if (u && (ADMIN_UIDS.includes(u.uid) || (await u.getIdTokenResult()).claims?.role === 'admin')) {
        setIsAdmin(true);
      } else {
        setIsAdmin(false);
        setLoading(false);
      }
    });
    return () => unsub();
  }, []);

  const loadData = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    const db = getFirestore();

    try {
      // Load audit logs
      let q = auditFilter === 'all'
        ? query(collection(db, 'audit_log'), orderBy('createdAt', 'desc'), limit(200))
        : query(collection(db, 'audit_log'), where('event', '==', auditFilter), orderBy('createdAt', 'desc'), limit(200));
      const auditSnap = await getDocs(q);
      setAuditLogs(auditSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Security stats from all logs
      const allAuditQ = query(collection(db, 'audit_log'), orderBy('createdAt', 'desc'), limit(1000));
      const allAuditSnap = await getDocs(allAuditQ);
      const allLogs = allAuditSnap.docs.map(d => d.data());
      setSecurityStats({
        totalCalls: allLogs.filter(l => l.event === 'api_call').length,
        abuseFlags: allLogs.filter(l => l.event === 'abuse_flag' || l.event === 'abuse_blocked').length,
        rateLimits: allLogs.filter(l => l.event === 'rate_limited').length,
        authFailures: allLogs.filter(l => l.event === 'auth_failed').length,
      });

      // Load user balances + compute revenue stats
      const usersSnap = await getDocs(query(collection(db, 'users'), limit(100)));
      const users = usersSnap.docs.map(d => ({ uid: d.id, ...d.data() }));
      const withTokens = users.filter(u => (u.computeTokenLifetime || 0) > 0 || (u.computeTokenBalance || 0) > 0);

      setUserBalances(withTokens.map(u => ({
        uid: u.uid,
        email: u.email || u.displayName || u.uid.slice(0, 12),
        balance: u.computeTokenBalance || 0,
        lifetime: u.computeTokenLifetime || 0,
        consumed: (u.computeTokenLifetime || 0) - (u.computeTokenBalance || 0),
        tier: u.subscriptionTier || 'free',
        tierGrantedByAdmin: u.tierGrantedByAdmin === true,
        roles: u.roles || [],
        hasQuotaOverride: !!u.quotaOverride,
      })).sort((a, b) => b.consumed - a.consumed));

      // Calculate revenue metrics from api_call audit logs
      const apiCalls = allLogs.filter(l => l.event === 'api_call' && l.tokens);
      const totalTokensConsumed = apiCalls.reduce((sum, l) => sum + (l.tokens || 0), 0);
      const totalCostToUs = totalTokensConsumed * 0.01 / 1.20; // Remove markup to get raw cost
      const totalRevenue = totalTokensConsumed * 0.01; // Token value
      const totalTokensSold = withTokens.reduce((sum, u) => sum + (u.computeTokenLifetime || 0), 0);

      // Per-provider breakdown from audit logs
      const byProvider = {};
      apiCalls.forEach(l => {
        const key = l.model || l.endpoint || 'unknown';
        if (!byProvider[key]) byProvider[key] = { calls: 0, tokens: 0 };
        byProvider[key].calls++;
        byProvider[key].tokens += l.tokens || 0;
      });

      setRevenueStats({
        totalTokensSold,
        totalRevenue: totalRevenue.toFixed(2),
        totalTokensConsumed: totalTokensConsumed.toFixed(2),
        totalCostToUs: totalCostToUs.toFixed(2),
        grossMargin: totalRevenue > 0 ? ((1 - totalCostToUs / totalRevenue) * 100).toFixed(1) : '20.0',
        activeUsers: withTokens.length,
        avgTokensPerUser: withTokens.length > 0 ? (totalTokensConsumed / withTokens.length).toFixed(1) : '0',
        byProvider,
      });

    } catch (err) {
      console.error('Admin data load failed:', err);
    }
    setLoading(false);
  }, [isAdmin, auditFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleLookup = async () => {
    if (!lookupEmail.trim()) return;
    setLookupLoading(true);
    setLookupError('');
    setLookupResult(null);
    setDeleteConfirm(false);
    try {
      const functions = getFunctions();
      const lookup = httpsCallable(functions, 'adminUserLookup');
      const result = await lookup({ email: lookupEmail.trim() });
      setLookupResult(result.data);
    } catch (err) {
      setLookupError(err.message);
    }
    setLookupLoading(false);
  };

  const handleAdminDelete = async () => {
    if (!lookupResult?.uid) return;
    setDeleteLoading(true);
    setLookupError('');
    try {
      const isAdminTarget = ['GI1GHQ7wpLQKjJyf9QRQATyw5e33', 'vGB6BL3iJfh32aWZ0HA93nUV2013'].includes(lookupResult.uid);
      const functions = getFunctions();
      const deleteFn = httpsCallable(functions, 'adminDeleteUser');
      await deleteFn({ targetUid: lookupResult.uid, confirm: true, forceAdmin: isAdminTarget });
      setLookupResult(null);
      setDeleteConfirm(false);
      setLookupError('');
      setLookupEmail('');
      alert(`Account ${lookupResult.email} deleted successfully.`);
    } catch (err) {
      setLookupError(err.message);
    }
    setDeleteLoading(false);
  };

  // Admin update — tier, roles, quota override. One callable per change so
  // the audit log records each delta individually.
  const handleAdminUpdate = async (targetUid, change) => {
    setUpdateLoading(true);
    setUpdateStatus('');
    try {
      const update = httpsCallable(getFunctions(), 'adminUpdateUser');
      const { data } = await update({ targetUid, ...change });
      setUpdateStatus(`Updated ${targetUid.slice(0,8)}…: ${Object.keys(data.changes || {}).join(', ')}`);
      // Refresh in 500ms so Firestore writes propagate to the table
      setTimeout(() => loadData(), 500);
    } catch (err) {
      setUpdateStatus(`Error: ${err.message || err}`);
    }
    setUpdateLoading(false);
  };

  if (!user) {
    return <div className="mp-page" style={{ background: '#0f172a', color: '#f8fafc', textAlign: 'center', padding: '80px 24px' }}>
      <h1 style={{ color: '#f8fafc', fontSize: '2rem' }}>Admin Dashboard</h1>
      <p style={{ color: '#94a3b8' }}>Sign in to access.</p>
    </div>;
  }

  if (!isAdmin) {
    return <div className="mp-page" style={{ background: '#0f172a', color: '#f8fafc', textAlign: 'center', padding: '80px 24px' }}>
      <h1 style={{ color: '#f8fafc', fontSize: '2rem' }}>Access Denied</h1>
      <p style={{ color: '#94a3b8' }}>Administrators only.</p>
    </div>;
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'accounts', label: 'Accounts' },
    { id: 'revenue', label: 'Revenue & Cost' },
    { id: 'users', label: 'Users' },
    { id: 'security', label: 'Security' },
  ];

  const eventColors = {
    api_call: '#22c55e', auth_failed: '#ef4444', rate_limited: '#f59e0b',
    abuse_flag: '#ef4444', abuse_blocked: '#dc2626', ip_change: '#6366f1', skill_error: '#f97316',
  };
  const eventLabels = {
    api_call: 'API Call', auth_failed: 'Auth Failed', rate_limited: 'Rate Limited',
    abuse_flag: 'Abuse Flag', abuse_blocked: 'Abuse Blocked', ip_change: 'IP Change', skill_error: 'Skill Error',
  };

  const cardStyle = (color) => ({
    background: '#1e293b', borderRadius: 12, padding: 20, borderLeft: `4px solid ${color}`,
  });

  return (
    <div className="mp-page" style={{ background: '#0f172a', color: '#f8fafc' }}>
      <section style={{ maxWidth: 1200, margin: '0 auto', padding: '40px 24px' }}>
        <h1 style={{ color: '#f8fafc', fontSize: '2rem', fontWeight: 800, marginBottom: 8 }}>Admin Dashboard</h1>
        <p style={{ color: '#64748b', marginBottom: 12 }}>mChatAI Cloud — Operations & Revenue</p>
        <AdminPageDirectory />
        <div style={{ marginBottom: 24, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <ModelCatalogSyncPill />
          <NewsFeedsSyncPill />
        </div>

        {/* Tab Bar */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 32, borderBottom: '1px solid #334155', paddingBottom: 2 }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
              padding: '8px 20px', border: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
              borderRadius: '8px 8px 0 0',
              background: activeTab === t.id ? '#1e293b' : 'transparent',
              color: activeTab === t.id ? '#f8fafc' : '#64748b',
            }}>{t.label}</button>
          ))}
        </div>

        {loading ? (
          <div style={{ color: '#94a3b8', padding: 60, textAlign: 'center' }}>Loading dashboard data...</div>
        ) : (
          <>
            {/* ── OVERVIEW TAB ── */}
            {activeTab === 'overview' && (
              <>
                {/* Phase IM.4 — provider routing + cost watch panel */}
                <ProviderRoutingPanel />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 32 }}>
                  <div style={cardStyle('#22c55e')}>
                    <div style={{ color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase' }}>Revenue</div>
                    <div style={{ color: '#f8fafc', fontSize: '1.8rem', fontWeight: 800 }}>${revenueStats.totalRevenue}</div>
                  </div>
                  <div style={cardStyle('#3b82f6')}>
                    <div style={{ color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase' }}>Tokens Consumed</div>
                    <div style={{ color: '#f8fafc', fontSize: '1.8rem', fontWeight: 800 }}>{revenueStats.totalTokensConsumed}</div>
                  </div>
                  <div style={cardStyle(parseFloat(revenueStats.grossMargin) >= 18 ? '#22c55e' : '#ef4444')}>
                    <div style={{ color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase' }}>Gross Margin</div>
                    <div style={{ color: '#f8fafc', fontSize: '1.8rem', fontWeight: 800 }}>{revenueStats.grossMargin}%</div>
                  </div>
                  <div style={cardStyle('#8b5cf6')}>
                    <div style={{ color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase' }}>Active Users</div>
                    <div style={{ color: '#f8fafc', fontSize: '1.8rem', fontWeight: 800 }}>{revenueStats.activeUsers}</div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
                  <div style={cardStyle('#22c55e')}>
                    <div style={{ color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase' }}>API Calls</div>
                    <div style={{ color: '#f8fafc', fontSize: '1.8rem', fontWeight: 800 }}>{securityStats.totalCalls}</div>
                  </div>
                  <div style={cardStyle(securityStats.abuseFlags > 0 ? '#ef4444' : '#22c55e')}>
                    <div style={{ color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase' }}>Abuse Flags</div>
                    <div style={{ color: '#f8fafc', fontSize: '1.8rem', fontWeight: 800 }}>{securityStats.abuseFlags}</div>
                  </div>
                  <div style={cardStyle('#f59e0b')}>
                    <div style={{ color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase' }}>Rate Limits</div>
                    <div style={{ color: '#f8fafc', fontSize: '1.8rem', fontWeight: 800 }}>{securityStats.rateLimits}</div>
                  </div>
                  <div style={cardStyle('#64748b')}>
                    <div style={{ color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase' }}>Cost to Us</div>
                    <div style={{ color: '#f8fafc', fontSize: '1.8rem', fontWeight: 800 }}>${revenueStats.totalCostToUs}</div>
                  </div>
                </div>
              </>
            )}

            {/* ── ACCOUNTS TAB ── */}
            {activeTab === 'accounts' && (
              <>
                <h3 style={{ color: '#f8fafc', fontSize: '1rem', fontWeight: 600, margin: '0 0 16px' }}>User Account Lookup</h3>
                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                  <input
                    type="email" placeholder="Search by email address..."
                    value={lookupEmail} onChange={(e) => setLookupEmail(e.target.value)}
                    style={{
                      flex: 1, padding: '10px 14px', borderRadius: 8, border: '1px solid #334155',
                      background: '#0f172a', color: '#f8fafc', fontSize: '0.85rem', fontFamily: 'inherit', outline: 'none',
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && lookupEmail.trim() && handleLookup()}
                  />
                  <button onClick={handleLookup} disabled={lookupLoading || !lookupEmail.trim()} style={{
                    background: '#3b82f6', color: '#fff', border: 'none', padding: '10px 20px',
                    borderRadius: 8, fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'inherit',
                    opacity: lookupLoading ? 0.6 : 1,
                  }}>
                    {lookupLoading ? 'Searching...' : 'Lookup'}
                  </button>
                </div>
                {lookupError && <p style={{ color: '#f87171', fontSize: '0.8rem', marginBottom: 12 }}>{lookupError}</p>}

                {lookupResult && lookupResult.notFound && (
                  <div style={{ background: '#1e293b', borderRadius: 12, padding: 20, border: '1px solid #334155' }}>
                    <p style={{ color: '#f59e0b', fontSize: '0.9rem', margin: 0 }}>
                      No Firebase account found for <strong style={{ color: '#f8fafc' }}>{lookupResult.email}</strong>
                    </p>
                  </div>
                )}

                {lookupResult && !lookupResult.notFound && (
                  <div style={{ background: '#1e293b', borderRadius: 12, padding: 20, border: '1px solid #334155' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                      <div style={{
                        width: 40, height: 40, borderRadius: '50%', background: '#3b82f6',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontWeight: 700, fontSize: '1rem',
                      }}>
                        {(lookupResult.displayName || lookupResult.email || 'U')[0].toUpperCase()}
                      </div>
                      <div>
                        <div style={{ color: '#f8fafc', fontWeight: 600 }}>{lookupResult.displayName || 'No name'}</div>
                        <div style={{ color: '#64748b', fontSize: '0.8rem' }}>{lookupResult.email}</div>
                        {lookupResult.handle && <div style={{ color: '#475569', fontSize: '0.75rem' }}>@{lookupResult.handle}</div>}
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16, fontSize: '0.8rem' }}>
                      <div><span style={{ color: '#64748b' }}>UID:</span> <span style={{ color: '#f8fafc' }}>{lookupResult.uid}</span></div>
                      <div><span style={{ color: '#64748b' }}>Tier:</span> <span style={{ color: '#f8fafc' }}>{lookupResult.subscriptionTier}</span></div>
                      <div><span style={{ color: '#64748b' }}>Tokens:</span> <span style={{ color: '#f8fafc' }}>{lookupResult.tokenBalance.toFixed(1)} / {lookupResult.tokenLifetime.toFixed(1)} lifetime</span></div>
                      <div><span style={{ color: '#64748b' }}>Verified:</span> <span style={{ color: lookupResult.emailVerified ? '#10b981' : '#f59e0b' }}>{lookupResult.emailVerified ? 'Yes' : 'No'}</span></div>
                      <div><span style={{ color: '#64748b' }}>Created:</span> <span style={{ color: '#f8fafc' }}>{lookupResult.created}</span></div>
                      <div><span style={{ color: '#64748b' }}>Last sign-in:</span> <span style={{ color: '#f8fafc' }}>{lookupResult.lastSignIn}</span></div>
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', marginBottom: 6 }}>Sign-In Methods</div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {lookupResult.providers.map(p => (
                          <span key={p.providerId} style={{
                            background: '#0f172a', padding: '4px 10px', borderRadius: 6, fontSize: '0.75rem',
                            color: '#f8fafc', border: '1px solid #334155',
                          }}>
                            {p.providerId === 'password' ? 'Email/Password' : p.providerId === 'apple.com' ? 'Apple' : p.providerId === 'google.com' ? 'Google' : p.providerId}
                            {p.email && <span style={{ color: '#64748b', marginLeft: 6 }}>{p.email}</span>}
                          </span>
                        ))}
                      </div>
                    </div>
                    {/* Delete button */}
                    {!deleteConfirm ? (
                      <button onClick={() => setDeleteConfirm(true)} style={{
                        background: 'transparent', border: '1px solid #dc2626', color: '#f87171',
                        padding: '6px 16px', borderRadius: 8, fontSize: '0.78rem', fontWeight: 600,
                        cursor: 'pointer', fontFamily: 'inherit',
                      }}>
                        Delete This Account
                      </button>
                    ) : (
                      <div style={{
                        background: 'rgba(220, 38, 38, 0.06)', border: '1px solid rgba(220, 38, 38, 0.2)',
                        borderRadius: 8, padding: 12,
                      }}>
                        <p style={{ color: '#f87171', fontSize: '0.8rem', margin: '0 0 8px' }}>
                          Permanently delete <strong>{lookupResult.email}</strong> ({lookupResult.tokenBalance.toFixed(1)} tokens)?
                        </p>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button onClick={handleAdminDelete} disabled={deleteLoading} style={{
                            background: '#dc2626', color: '#fff', border: 'none', padding: '6px 16px',
                            borderRadius: 8, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                            opacity: deleteLoading ? 0.6 : 1,
                          }}>
                            {deleteLoading ? 'Deleting...' : 'Confirm Delete'}
                          </button>
                          <button onClick={() => setDeleteConfirm(false)} style={{
                            background: 'transparent', border: '1px solid #334155', color: '#94a3b8',
                            padding: '6px 12px', borderRadius: 8, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit',
                          }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* ── REVENUE TAB ── */}
            {activeTab === 'revenue' && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 32 }}>
                  <div style={cardStyle('#22c55e')}>
                    <div style={{ color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase' }}>Tokens Sold (Lifetime)</div>
                    <div style={{ color: '#f8fafc', fontSize: '1.8rem', fontWeight: 800 }}>{revenueStats.totalTokensSold.toFixed(0)}</div>
                    <div style={{ color: '#64748b', fontSize: '0.75rem' }}>${(revenueStats.totalTokensSold * 0.01).toFixed(2)} value</div>
                  </div>
                  <div style={cardStyle('#3b82f6')}>
                    <div style={{ color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase' }}>Tokens Consumed</div>
                    <div style={{ color: '#f8fafc', fontSize: '1.8rem', fontWeight: 800 }}>{revenueStats.totalTokensConsumed}</div>
                    <div style={{ color: '#64748b', fontSize: '0.75rem' }}>${revenueStats.totalRevenue} revenue</div>
                  </div>
                  <div style={cardStyle('#f59e0b')}>
                    <div style={{ color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase' }}>Avg per User</div>
                    <div style={{ color: '#f8fafc', fontSize: '1.8rem', fontWeight: 800 }}>{revenueStats.avgTokensPerUser}</div>
                    <div style={{ color: '#64748b', fontSize: '0.75rem' }}>tokens consumed</div>
                  </div>
                </div>

                {/* Per-Model/Endpoint Breakdown */}
                <h3 style={{ color: '#e2e8f0', marginBottom: 12 }}>Usage by Model/Endpoint</h3>
                <div style={{ background: '#1e293b', borderRadius: 12, overflow: 'hidden', marginBottom: 32 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #334155' }}>
                        {['Model/Endpoint', 'Calls', 'Tokens', 'Revenue', 'Cost to Us'].map(h => (
                          <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(revenueStats.byProvider || {}).sort((a, b) => b[1].tokens - a[1].tokens).map(([key, data]) => (
                        <tr key={key} style={{ borderBottom: '1px solid #1e293b' }}>
                          <td style={{ padding: '8px 14px', color: '#e2e8f0', fontSize: '0.85rem', fontFamily: 'monospace' }}>{key}</td>
                          <td style={{ padding: '8px 14px', color: '#94a3b8', fontSize: '0.85rem' }}>{data.calls}</td>
                          <td style={{ padding: '8px 14px', color: '#f8fafc', fontSize: '0.85rem', fontWeight: 600 }}>{data.tokens.toFixed(2)}</td>
                          <td style={{ padding: '8px 14px', color: '#22c55e', fontSize: '0.85rem' }}>${(data.tokens * 0.01).toFixed(4)}</td>
                          <td style={{ padding: '8px 14px', color: '#64748b', fontSize: '0.85rem' }}>${(data.tokens * 0.01 / 1.20).toFixed(4)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {Object.keys(revenueStats.byProvider || {}).length === 0 && (
                    <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>No usage data yet.</div>
                  )}
                </div>
              </>
            )}

            {/* ── USERS TAB ── */}
            {activeTab === 'users' && (
              <>
                {/* Grant Tokens Form */}
                <div style={{ background: '#1e293b', borderRadius: 12, padding: 20, marginBottom: 24, border: '1px solid #334155' }}>
                  <h3 style={{ color: '#e2e8f0', margin: '0 0 16px', fontSize: '1rem' }}>Grant Compute Tokens</h3>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div style={{ flex: '1 1 200px' }}>
                      <label style={{ color: '#64748b', fontSize: '0.7rem', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>User UID</label>
                      <input value={grantUid} onChange={e => setGrantUid(e.target.value)} placeholder="Paste UID or click a row below"
                        style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #334155', background: '#0f172a', color: '#f8fafc', fontSize: '0.85rem', fontFamily: 'monospace', boxSizing: 'border-box' }} />
                    </div>
                    <div style={{ flex: '0 0 120px' }}>
                      <label style={{ color: '#64748b', fontSize: '0.7rem', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Amount</label>
                      <input type="number" value={grantAmount} onChange={e => setGrantAmount(e.target.value)} placeholder="1000"
                        style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #334155', background: '#0f172a', color: '#f8fafc', fontSize: '0.85rem', boxSizing: 'border-box' }} />
                    </div>
                    <div style={{ flex: '1 1 180px' }}>
                      <label style={{ color: '#64748b', fontSize: '0.7rem', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Reason (optional)</label>
                      <input value={grantReason} onChange={e => setGrantReason(e.target.value)} placeholder="Testing, promo, etc."
                        style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #334155', background: '#0f172a', color: '#f8fafc', fontSize: '0.85rem', boxSizing: 'border-box' }} />
                    </div>
                    <button disabled={grantLoading || !grantUid || !grantAmount} onClick={async () => {
                      setGrantLoading(true);
                      setGrantStatus('');
                      try {
                        const functions = getFunctions();
                        const grant = httpsCallable(functions, 'adminGrantTokens');
                        const result = await grant({ targetUid: grantUid.trim(), amount: Number(grantAmount), reason: grantReason || undefined });
                        setGrantStatus(`Granted ${grantAmount} tokens. Balance: ${result.data.balanceBefore} \u2192 ${result.data.balanceAfter}`);
                        setGrantAmount('');
                        setGrantReason('');
                        loadData(); // refresh table
                      } catch (err) {
                        setGrantStatus(`Error: ${err.message}`);
                      }
                      setGrantLoading(false);
                    }} style={{
                      padding: '8px 24px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
                      background: grantLoading || !grantUid || !grantAmount ? '#334155' : '#22c55e', color: '#fff',
                      opacity: grantLoading || !grantUid || !grantAmount ? 0.5 : 1, whiteSpace: 'nowrap',
                    }}>
                      {grantLoading ? 'Granting...' : 'Grant Tokens'}
                    </button>
                  </div>
                  {grantStatus && (
                    <div style={{ marginTop: 10, fontSize: '0.8rem', color: grantStatus.startsWith('Error') ? '#f87171' : '#22c55e' }}>{grantStatus}</div>
                  )}
                </div>

                {/* User search */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <h3 style={{ color: '#e2e8f0', margin: 0 }}>User Balances</h3>
                  <input value={userSearch} onChange={e => setUserSearch(e.target.value)} placeholder="Search by email or UID..."
                    style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #334155', background: '#0f172a', color: '#f8fafc', fontSize: '0.8rem', width: 240 }} />
                </div>
                {updateStatus && (
                  <div style={{ marginBottom: 10, padding: '8px 12px', borderRadius: 6, fontSize: '0.8rem',
                    background: updateStatus.startsWith('Error') ? '#7f1d1d' : '#14532d',
                    color: updateStatus.startsWith('Error') ? '#fecaca' : '#bbf7d0' }}>
                    {updateStatus}
                  </div>
                )}
                <div style={{ background: '#1e293b', borderRadius: 12, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #334155' }}>
                        {['User', 'UID', 'Tier', 'Balance', 'Lifetime', 'Consumed', 'Actions'].map(h => (
                          <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {userBalances
                        .filter(u => !userSearch || u.email.toLowerCase().includes(userSearch.toLowerCase()) || u.uid.toLowerCase().includes(userSearch.toLowerCase()))
                        .map(u => {
                          const tierColors = {
                            free:    { bg: '#33415522', fg: '#64748b' },
                            creator: { bg: '#0ea5e922', fg: '#38bdf8' },
                            pro:     { bg: '#3b82f622', fg: '#3b82f6' },
                            admin:   { bg: '#f59e0b22', fg: '#fbbf24' },
                          };
                          const tc = tierColors[u.tier] || { bg: '#8b5cf622', fg: '#a78bfa' }; // unknown / legacy
                          const isEditing = editingUid === u.uid;
                          const isLegacyTier = !['free','creator','pro','admin'].includes(u.tier);
                          return (
                            <React.Fragment key={u.uid}>
                              <tr style={{ borderBottom: isEditing ? 'none' : '1px solid #1e293b' }}>
                                <td style={{ padding: '8px 14px', color: '#e2e8f0', fontSize: '0.85rem' }}>{u.email}</td>
                                <td style={{ padding: '8px 14px', color: '#64748b', fontSize: '0.75rem', fontFamily: 'monospace' }}>{u.uid.slice(0, 10)}...</td>
                                <td style={{ padding: '8px 14px' }}>
                                  <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: '0.7rem', fontWeight: 700, background: tc.bg, color: tc.fg }}>
                                    {u.tier}
                                  </span>
                                  {isLegacyTier && (
                                    <span title="Legacy tier — normalizes to Free at runtime" style={{ marginLeft: 6, fontSize: '0.65rem', color: '#fbbf24' }}>⚠ legacy</span>
                                  )}
                                  {u.tierGrantedByAdmin && (
                                    <span title="Tier was set by admin, not via Stripe" style={{ marginLeft: 6, fontSize: '0.65rem', color: '#94a3b8' }}>(admin)</span>
                                  )}
                                </td>
                                <td style={{ padding: '8px 14px', color: '#f8fafc', fontSize: '0.85rem', fontWeight: 600 }}>{u.balance.toFixed(1)}</td>
                                <td style={{ padding: '8px 14px', color: '#94a3b8', fontSize: '0.85rem' }}>{u.lifetime.toFixed(1)}</td>
                                <td style={{ padding: '8px 14px', color: '#22c55e', fontSize: '0.85rem' }}>{u.consumed.toFixed(1)}</td>
                                <td style={{ padding: '8px 14px', display: 'flex', gap: 6 }}>
                                  <button onClick={() => setGrantUid(u.uid)} style={{
                                    padding: '4px 10px', borderRadius: 6, fontSize: '0.7rem', fontWeight: 600,
                                    background: grantUid === u.uid ? '#3b82f6' : 'transparent',
                                    color: grantUid === u.uid ? '#fff' : '#94a3b8',
                                    border: '1px solid #334155', cursor: 'pointer',
                                  }}>{grantUid === u.uid ? 'Grant target' : 'For grant'}</button>
                                  <button onClick={() => setEditingUid(isEditing ? '' : u.uid)} style={{
                                    padding: '4px 10px', borderRadius: 6, fontSize: '0.7rem', fontWeight: 600,
                                    background: isEditing ? '#7c3aed' : 'transparent',
                                    color: isEditing ? '#fff' : '#a78bfa',
                                    border: '1px solid #4c1d95', cursor: 'pointer',
                                  }}>{isEditing ? 'Close' : 'Edit'}</button>
                                </td>
                              </tr>
                              {isEditing && (
                                <tr style={{ borderBottom: '1px solid #1e293b', background: '#0f172a' }}>
                                  <td colSpan={7} style={{ padding: '14px 18px' }}>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'center', fontSize: '0.8rem', color: '#e2e8f0' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{ color: '#94a3b8' }}>Tier:</span>
                                        <select
                                          value={['free','creator','pro','admin'].includes(u.tier) ? u.tier : 'free'}
                                          disabled={updateLoading}
                                          onChange={e => handleAdminUpdate(u.uid, { subscriptionTier: e.target.value })}
                                          style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #334155', background: '#1e293b', color: '#f8fafc', fontSize: '0.8rem' }}
                                        >
                                          <option value="free">Free</option>
                                          <option value="creator">Creator</option>
                                          <option value="pro">Pro</option>
                                          <option value="admin">Admin</option>
                                        </select>
                                      </div>

                                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{ color: '#94a3b8' }}>Roles:</span>
                                        {['admin', 'compute_tokens_beta'].map(role => {
                                          const has = (u.roles || []).includes(role);
                                          return (
                                            <label key={role} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                                              <input
                                                type="checkbox"
                                                checked={has}
                                                disabled={updateLoading}
                                                onChange={() => handleAdminUpdate(u.uid, has ? { removeRole: role } : { addRole: role })}
                                              />
                                              <span style={{ color: '#cbd5e1' }}>{role}</span>
                                            </label>
                                          );
                                        })}
                                      </div>

                                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{ color: '#94a3b8' }}>Quota override:</span>
                                        {u.hasQuotaOverride ? (
                                          <>
                                            <span style={{ color: '#fbbf24', fontSize: '0.75rem' }}>Caps lifted</span>
                                            <button
                                              onClick={() => handleAdminUpdate(u.uid, { clearQuotaOverride: true })}
                                              disabled={updateLoading}
                                              style={{ padding: '3px 8px', borderRadius: 5, fontSize: '0.7rem', border: '1px solid #334155', background: 'transparent', color: '#94a3b8', cursor: 'pointer' }}
                                            >Restore tier caps</button>
                                          </>
                                        ) : (
                                          <button
                                            onClick={() => handleAdminUpdate(u.uid, { liftQuotaCaps: true })}
                                            disabled={updateLoading}
                                            style={{ padding: '3px 8px', borderRadius: 5, fontSize: '0.7rem', border: '1px solid #4c1d95', background: 'transparent', color: '#a78bfa', cursor: 'pointer' }}
                                          >Lift all caps (unlimited)</button>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                    </tbody>
                  </table>
                  {userBalances.length === 0 && (
                    <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>No users with tokens yet.</div>
                  )}
                </div>
              </>
            )}

            {/* ── SECURITY TAB ── */}
            {activeTab === 'security' && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
                  {[
                    { label: 'API Calls', value: securityStats.totalCalls, color: '#22c55e' },
                    { label: 'Abuse Flags', value: securityStats.abuseFlags, color: securityStats.abuseFlags > 0 ? '#ef4444' : '#22c55e' },
                    { label: 'Rate Limits', value: securityStats.rateLimits, color: securityStats.rateLimits > 10 ? '#f59e0b' : '#22c55e' },
                    { label: 'Auth Failures', value: securityStats.authFailures, color: securityStats.authFailures > 5 ? '#ef4444' : '#22c55e' },
                  ].map(s => (
                    <div key={s.label} style={cardStyle(s.color)}>
                      <div style={{ color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase' }}>{s.label}</div>
                      <div style={{ color: '#f8fafc', fontSize: '1.8rem', fontWeight: 800 }}>{s.value}</div>
                    </div>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                  {['all', 'api_call', 'auth_failed', 'rate_limited', 'abuse_flag', 'abuse_blocked', 'ip_change', 'skill_error'].map(f => (
                    <button key={f} onClick={() => setAuditFilter(f)} style={{
                      padding: '6px 14px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
                      background: auditFilter === f ? '#3b82f6' : '#334155',
                      color: auditFilter === f ? '#fff' : '#94a3b8',
                    }}>{f === 'all' ? 'All' : (eventLabels[f] || f)}</button>
                  ))}
                </div>

                <div style={{ background: '#1e293b', borderRadius: 12, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #334155' }}>
                        {['Time', 'Event', 'User', 'Endpoint', 'Details'].map(h => (
                          <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {auditLogs.map(log => (
                        <tr key={log.id} style={{ borderBottom: '1px solid #1e293b' }}>
                          <td style={{ padding: '8px 14px', color: '#94a3b8', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                            {log.timestamp ? new Date(log.timestamp).toLocaleString() : '\u2014'}
                          </td>
                          <td style={{ padding: '8px 14px' }}>
                            <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: '0.7rem', fontWeight: 700,
                              background: `${eventColors[log.event] || '#64748b'}22`, color: eventColors[log.event] || '#64748b',
                            }}>{eventLabels[log.event] || log.event}</span>
                          </td>
                          <td style={{ padding: '8px 14px', color: '#e2e8f0', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                            {log.uid ? `${log.uid.slice(0, 8)}...` : '\u2014'}
                          </td>
                          <td style={{ padding: '8px 14px', color: '#94a3b8', fontSize: '0.8rem' }}>{log.endpoint || '\u2014'}</td>
                          <td style={{ padding: '8px 14px', color: '#64748b', fontSize: '0.75rem', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {log.model && `model: ${log.model}`}
                            {log.tokens && ` | ${log.tokens.toFixed(2)} tkns`}
                            {log.reason && log.reason}
                            {log.ip && ` | ip: ${log.ip}`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {auditLogs.length === 0 && <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>No events.</div>}
                </div>
              </>
            )}
          </>
        )}

        <p style={{ color: '#475569', fontSize: '0.7rem', marginTop: 24, textAlign: 'center' }}>
          mChatAI Cloud Admin \u2014 Phase CC.A + CC.SEC
        </p>
      </section>
    </div>
  );
}
